import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  TextField,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

function AdminContent() {
  const [file, setFile] = useState(null); // Para o arquivo selecionado
  const [loading, setLoading] = useState(false); // Controle de loading durante o upload
  const [currentFile, setCurrentFile] = useState(null); // Dados do arquivo atual no backend
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" }); // Feedback para o usuário

  // Função para buscar o arquivo atual
  const fetchCurrentFile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/files`);
      if (response.ok) {
        const data = await response.json();
        setCurrentFile(data);
      } else {
        setCurrentFile(null);
      }
    } catch (error) {
      console.error("Erro ao buscar o arquivo atual:", error);
      setCurrentFile(null);
    }
  };

  // Buscar o arquivo atual ao montar o componente
  useEffect(() => {
    fetchCurrentFile();
  }, []);

  // Lidar com seleção de arquivo
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Lidar com o envio do arquivo
  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setSnackbar({ open: true, message: "Por favor, selecione um arquivo.", severity: "warning" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/files`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.ok) {
        //const data = await response.json();
        setSnackbar({ open: true, message: "Arquivo enviado com sucesso!", severity: "success" });
        setFile(null);
        fetchCurrentFile(); // Atualiza o arquivo atual
      } else {
        const errorData = await response.json();
        setSnackbar({
          open: true,
          message: `Erro ao enviar o arquivo: ${errorData.message || "Desconhecido"}`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Erro ao enviar o arquivo:", error);
      setSnackbar({
        open: true,
        message: "Erro no servidor. Tente novamente.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fechar o snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAdminDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/files/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Erro ao baixar arquivo.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = currentFile.original_filename || currentFile.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Erro ao baixar arquivo.",
        severity: "error",
      });
    }
  };

  return (
    <Paper
      sx={{
        padding: 3,
        backgroundColor: "#0D1B2A",
        mx: { xs: 1, sm: 0 },
      }}
    >
      <Typography variant="h6" gutterBottom>
        Gerenciador de Arquivos
      </Typography>
      <Box component="form" onSubmit={handleUpload} sx={{ marginTop: 2 }}>
        <TextField
          type="file"
          fullWidth
          onChange={handleFileChange}
          sx={{ marginBottom: 2 }}
          inputProps={{ accept: ".psf" }} // Restringe a seleção ao formato .psf
        />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading} // Desabilita durante o upload
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? "Enviando..." : "Enviar Arquivo"}
        </Button>
      </Box>

      {/* Exibição do arquivo atual */}
      {currentFile && (
        <Box mt={3}>
          <Typography variant="subtitle1">
            Arquivo atual: <strong>{currentFile.filename}</strong>
          </Typography>
          <Typography variant="body2">
            Última atualização: {new Date(currentFile.uploaded_at).toLocaleString()}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAdminDownload}
            sx={{ mt: 2 }}
          >
            Baixar Arquivo
          </Button>
        </Box>
      )}

      {/* Feedback para o usuário */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default AdminContent;
